import { useState, useEffect, useRef } from "react";

const COLORS = {
  bg: "#0a0e1a",
  bgCard: "#111827",
  bgCardAlt: "#1a2236",
  border: "#1e293b",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  accent: "#3b82f6",
  accentGlow: "rgba(59,130,246,0.15)",
  success: "#22c55e",
  successGlow: "rgba(34,197,94,0.15)",
  warning: "#f59e0b",
  warningGlow: "rgba(245,158,11,0.15)",
  danger: "#ef4444",
  dangerGlow: "rgba(239,68,68,0.15)",
  purple: "#a78bfa",
  purpleGlow: "rgba(167,139,250,0.15)",
  cyan: "#22d3ee",
};

const FONT = `'JetBrains Mono', 'Fira Code', 'SF Mono', monospace`;
const FONT_BODY = `'DM Sans', 'Segoe UI', system-ui, sans-serif`;

function Section({ num, title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        {num} — {title}
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 18, lineHeight: 1.6 }}>
        {subtitle}
      </p>
      {children}
    </div>
  );
}

function Terminal({ children, height }) {
  return (
    <div style={{
      background: "#000", borderRadius: 6, padding: "10px 14px",
      fontFamily: FONT, fontSize: 11.5, color: COLORS.success, whiteSpace: "pre-wrap",
      lineHeight: 1.7, border: `1px solid ${COLORS.border}`, minHeight: height || "auto",
      overflowX: "auto",
    }}>
      {children}
    </div>
  );
}

function ConceptSection() {
  const [active, setActive] = useState(0);
  const concepts = [
    {
      icon: "🔁",
      title: "A controller is a loop",
      color: COLORS.accent,
      glow: COLORS.accentGlow,
      content: `A controller is a program that runs forever in a loop. Every cycle it asks three questions: What did the user ASK for? What is actually THERE right now? What do I change to close the gap? That is the entire idea. Everything else is detail.`,
    },
    {
      icon: "📝",
      title: "You declare, it reconciles",
      color: COLORS.purple,
      glow: COLORS.purpleGlow,
      content: `You never tell Kubernetes "create 3 pods". You write "replicas: 3" — a description of the world you want. A controller reads that wish and does whatever it takes, forever, to make reality match it. This is why K8s is called declarative.`,
    },
    {
      icon: "🎯",
      title: "Desired state vs actual state",
      color: COLORS.success,
      glow: COLORS.successGlow,
      content: `Desired state lives in the object's spec (what you wrote). Actual state lives in status (what the system observed). A controller's whole job is: watch spec, watch status, act until spec == status. Then go quiet and keep watching.`,
    },
    {
      icon: "🚫",
      title: "It never touches a node",
      color: COLORS.danger,
      glow: COLORS.dangerGlow,
      content: `Big beginner trap: the controller does NOT ssh into a node or start a container. It only talks to the API Server. It creates a Pod object; the scheduler picks a node; the kubelet on that node actually runs it. Controllers write records, not containers.`,
    },
  ];

  return (
    <Section num="01" title="WHAT IS A CONTROLLER?" subtitle="Four ideas. Tap each one. If you only keep one, keep the first.">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        {concepts.map((c, i) => (
          <div
            key={i}
            onClick={() => setActive(active === i ? null : i)}
            style={{
              background: active === i ? c.glow : COLORS.bgCard,
              border: `1px solid ${active === i ? c.color : COLORS.border}`,
              borderRadius: 10, padding: 16, cursor: "pointer", transition: "all 0.3s",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>{c.icon}</span>
              <span style={{ fontFamily: FONT, fontSize: 13, color: c.color, fontWeight: 700 }}>{c.title}</span>
            </div>
            <p style={{
              fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65,
              maxHeight: active === i ? 300 : 0, overflow: "hidden",
              transition: "max-height 0.4s ease", margin: 0,
            }}>
              {c.content}
            </p>
            {active !== i && (
              <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>tap to expand ▾</span>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function AnalogySection() {
  const [step, setStep] = useState(0);
  const steps = [
    {
      emoji: "🌡️",
      title: "The thermostat on your wall",
      text: "You set it to 22°C. That's your desired state. You do not run to the boiler and turn it on yourself — you just declared what you want the room to feel like.",
      highlight: COLORS.accent,
    },
    {
      emoji: "👀",
      title: "It reads the room, forever",
      text: "The thermostat has one sensor and one job: check the actual temperature, over and over, all day. Nobody has to press a button. Watching is the default state.",
      highlight: COLORS.purple,
    },
    {
      emoji: "➖",
      title: "It compares, and acts on the gap",
      text: "Actual 19°C, desired 22°C, gap = -3. So it turns the heater ON. It doesn't care WHY the room got cold — window open, winter, doesn't matter. It only closes the gap.",
      highlight: COLORS.warning,
    },
    {
      emoji: "😌",
      title: "Gap closed → it goes quiet",
      text: "At 22°C it switches the heater off and keeps watching. Open a window and it wakes up again. That is a Kubernetes controller: a thermostat for cluster objects.",
      highlight: COLORS.success,
    },
    {
      emoji: "🏢",
      title: "Now scale it up",
      text: "K8s doesn't have one thermostat — it has ~30, each watching one kind of thing. One watches replicas, one watches nodes, one watches Jobs, one watches storage. Same loop, different sensor.",
      highlight: COLORS.cyan,
    },
  ];
  const s = steps[step];

  return (
    <Section num="02" title="THE THERMOSTAT ANALOGY" subtitle="If you understand a thermostat, you already understand controllers. Step through it.">
      <div style={{
        background: COLORS.bgCard, border: `1px solid ${s.highlight}`, borderRadius: 12,
        padding: 24, position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0, width: `${((step + 1) / steps.length) * 100}%`,
          height: 3, background: s.highlight, transition: "width 0.5s ease",
        }} />
        <div style={{ fontSize: 48, marginBottom: 12, textAlign: "center" }}>{s.emoji}</div>
        <h3 style={{ fontFamily: FONT, fontSize: 15, color: s.highlight, textAlign: "center", marginBottom: 10 }}>{s.title}</h3>
        <p style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.text, textAlign: "center", lineHeight: 1.7, maxWidth: 520, margin: "0 auto 18px" }}>
          {s.text}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          {steps.map((_, i) => (
            <button key={i} onClick={() => setStep(i)} aria-label={`Step ${i + 1}`} style={{
              width: i === step ? 28 : 10, height: 10, borderRadius: 5, border: "none", cursor: "pointer",
              background: i === step ? s.highlight : COLORS.border, transition: "all 0.3s", padding: 0,
            }} />
          ))}
        </div>
      </div>
    </Section>
  );
}

function SimulatorSection() {
  const [desired, setDesired] = useState(3);
  const [pods, setPods] = useState([
    { id: 1, name: "web-app-a1", status: "Running" },
    { id: 2, name: "web-app-b2", status: "Running" },
    { id: 3, name: "web-app-c3", status: "Running" },
  ]);
  const [controllerOn, setControllerOn] = useState(true);
  const [cycle, setCycle] = useState(0);
  const [logs, setLogs] = useState([
    { t: 0, msg: "controller started — watching Deployment/web-app", color: COLORS.textDim },
  ]);
  const [pulse, setPulse] = useState(false);

  const uidRef = useRef(4);
  const podsRef = useRef(pods);
  const tickRef = useRef(null);
  podsRef.current = pods;

  const addLog = (msg, color) =>
    setLogs((l) => [...l.slice(-5), { t: Date.now() + Math.random(), msg, color }]);

  const randName = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    return `web-app-${chars[Math.floor(Math.random() * 26)]}${Math.floor(Math.random() * 9)}`;
  };

  tickRef.current = () => {
    setCycle((c) => c + 1);
    setPulse(true);
    setTimeout(() => setPulse(false), 400);

    let next = podsRef.current.filter((p) => p.status !== "Terminating");
    next = next.map((p) => (p.status === "Pending" ? { ...p, status: "Running" } : p));

    if (!controllerOn) {
      setPods(next);
      return;
    }

    const live = next.length;
    if (live < desired) {
      const missing = desired - live;
      addLog(`observed ${live}, desired ${desired} → creating ${missing} pod(s)`, COLORS.warning);
      for (let i = 0; i < missing; i++) {
        next.push({ id: uidRef.current++, name: randName(), status: "Pending" });
      }
    } else if (live > desired) {
      const extra = live - desired;
      addLog(`observed ${live}, desired ${desired} → deleting ${extra} pod(s)`, COLORS.danger);
      for (let i = 0; i < extra; i++) {
        next[next.length - 1 - i] = { ...next[next.length - 1 - i], status: "Terminating" };
      }
    }
    setPods(next);
  };

  useEffect(() => {
    const id = setInterval(() => tickRef.current(), 1600);
    return () => clearInterval(id);
  }, []);

  const killPod = () => {
    const running = podsRef.current.filter((p) => p.status === "Running");
    if (!running.length) return;
    const victim = running[Math.floor(Math.random() * running.length)];
    setPods((prev) => prev.filter((p) => p.id !== victim.id));
    addLog(`💥 node crashed — ${victim.name} is gone`, COLORS.danger);
  };

  const reset = () => {
    uidRef.current = 4;
    setDesired(3);
    setControllerOn(true);
    setPods([
      { id: 1, name: "web-app-a1", status: "Running" },
      { id: 2, name: "web-app-b2", status: "Running" },
      { id: 3, name: "web-app-c3", status: "Running" },
    ]);
    setCycle(0);
    setLogs([{ t: 0, msg: "controller restarted — watching Deployment/web-app", color: COLORS.textDim }]);
  };

  const liveCount = pods.filter((p) => p.status !== "Terminating").length;
  const inSync = liveCount === desired && pods.every((p) => p.status === "Running");
  const statusColor = { Running: COLORS.success, Pending: COLORS.warning, Terminating: COLORS.danger };

  const btn = (bg, color) => ({
    fontFamily: FONT, fontSize: 12, padding: "8px 14px", borderRadius: 6,
    border: bg === "transparent" ? `1px solid ${COLORS.border}` : "none",
    cursor: "pointer", fontWeight: 700, background: bg, color,
  });

  return (
    <Section
      num="03"
      title="LIVE CONTROL LOOP"
      subtitle="This runs on its own — a real reconcile loop, every 1.6s. Kill a pod and watch it come back. Then switch the controller off and kill one again."
    >
      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20 }}>

        {!controllerOn && (
          <div style={{
            background: COLORS.dangerGlow, border: `1px solid ${COLORS.danger}`, borderRadius: 8,
            padding: "8px 12px", marginBottom: 14, fontFamily: FONT, fontSize: 11.5, color: COLORS.danger,
          }}>
            ⚠ kube-controller-manager is DOWN — nobody is watching. Dead pods stay dead.
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{
            flex: "1 1 180px", background: COLORS.bgCardAlt, border: `2px solid ${COLORS.accent}`,
            borderRadius: 10, padding: 14,
          }}>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: 1, marginBottom: 8 }}>
              DESIRED STATE · spec
            </div>
            <div style={{ fontFamily: FONT, fontSize: 11.5, color: COLORS.textMuted, marginBottom: 10 }}>
              kind: Deployment<br />name: web-app
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => setDesired((d) => Math.max(0, d - 1))} style={btn(COLORS.border, COLORS.text)}>−</button>
              <div style={{ fontFamily: FONT, fontSize: 26, color: COLORS.accent, fontWeight: 700, minWidth: 30, textAlign: "center" }}>
                {desired}
              </div>
              <button onClick={() => setDesired((d) => Math.min(6, d + 1))} style={btn(COLORS.border, COLORS.text)}>+</button>
              <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>replicas</span>
            </div>
          </div>

          <div style={{
            flex: "0 0 100px", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 4,
          }}>
            <div style={{
              fontSize: 26, color: controllerOn ? COLORS.cyan : COLORS.textDim,
              transform: pulse ? "rotate(180deg) scale(1.25)" : "rotate(0) scale(1)",
              transition: "transform 0.4s ease",
            }}>↻</div>
            <div style={{ fontFamily: FONT, fontSize: 9.5, color: COLORS.textDim, textAlign: "center", lineHeight: 1.4 }}>
              reconcile<br />#{cycle}
            </div>
            <div style={{
              fontFamily: FONT, fontSize: 9, padding: "2px 6px", borderRadius: 4,
              background: inSync ? COLORS.successGlow : COLORS.warningGlow,
              color: inSync ? COLORS.success : COLORS.warning,
            }}>
              {inSync ? "IN SYNC" : "DRIFT"}
            </div>
          </div>

          <div style={{
            flex: "1 1 220px", background: COLORS.bgCardAlt,
            border: `2px solid ${inSync ? COLORS.success : COLORS.warning}`,
            borderRadius: 10, padding: 14, transition: "border-color 0.4s",
          }}>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, letterSpacing: 1, marginBottom: 8 }}>
              ACTUAL STATE · status
            </div>
            <div style={{ fontFamily: FONT, fontSize: 11.5, color: COLORS.textMuted, marginBottom: 10 }}>
              ReplicaSet: web-app-7d4f<br />live pods: {liveCount}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, minHeight: 60 }}>
              {pods.length === 0 && (
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>no pods</span>
              )}
              {pods.map((p) => (
                <div key={p.id} style={{
                  display: "flex", alignItems: "center", gap: 6,
                  fontFamily: FONT, fontSize: 10.5, color: COLORS.text,
                  background: COLORS.bgCard, borderRadius: 4, padding: "3px 8px",
                  borderLeft: `3px solid ${statusColor[p.status]}`,
                  opacity: p.status === "Terminating" ? 0.45 : 1, transition: "all 0.3s",
                }}>
                  <span style={{ flex: 1 }}>{p.name}</span>
                  <span style={{ color: statusColor[p.status], fontSize: 9.5 }}>{p.status}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Terminal height={96}>
          {logs.map((l, i) => (
            <div key={i} style={{ color: l.color }}>&gt; {l.msg}</div>
          ))}
        </Terminal>

        <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          <button onClick={killPod} style={btn(COLORS.danger, "#fff")}>💥 Kill a pod</button>
          <button
            onClick={() => {
              setControllerOn((v) => {
                addLog(v ? "controller stopped" : "controller resumed — watching again", v ? COLORS.danger : COLORS.success);
                return !v;
              });
            }}
            style={btn(controllerOn ? COLORS.border : COLORS.success, controllerOn ? COLORS.text : "#fff")}
          >
            {controllerOn ? "⏸ Stop controller" : "▶ Start controller"}
          </button>
          <button onClick={reset} style={btn("transparent", COLORS.textMuted)}>↺ Reset</button>
        </div>
      </div>

      <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7, marginTop: 12 }}>
        💡 Notice what you never did: you never created a replacement pod. You only ever changed the <strong style={{ color: COLORS.accent }}>number you want</strong>. Self-healing isn't magic — it's this loop, running whether you're watching or not.
      </p>
    </Section>
  );
}

function FamilySection() {
  const [open, setOpen] = useState(null);
  const controllers = [
    {
      name: "Deployment controller", icon: "📜", color: COLORS.accent,
      watches: "Deployment objects",
      does: "Creates and manages ReplicaSets. On an image change it makes a NEW ReplicaSet and shifts pods over gradually — that's a rolling update. It never creates pods itself.",
      hook: "The manager who hires team leads, not workers.",
    },
    {
      name: "ReplicaSet controller", icon: "👥", color: COLORS.success,
      watches: "ReplicaSets + the pods they own",
      does: "Counts pods matching its label selector. Too few → create. Too many → delete. This is the controller you just watched in the simulator.",
      hook: "The team lead who keeps exactly N people on shift.",
    },
    {
      name: "Node controller", icon: "🖥️", color: COLORS.warning,
      watches: "Node objects and their heartbeats",
      does: "If a node stops sending heartbeats it marks it NotReady, then taints it so its pods get evicted and rescheduled elsewhere. See section 06 for the exact clock.",
      hook: "The supervisor doing a roll call every few seconds.",
    },
    {
      name: "Job / CronJob controller", icon: "⏱️", color: COLORS.purple,
      watches: "Jobs and CronJobs",
      does: "Runs pods until N complete successfully, retries failures, and (for CronJob) creates a fresh Job on a schedule. Desired state here is 'finished', not 'running'.",
      hook: "The task list that isn't done until it's ticked off.",
    },
    {
      name: "EndpointSlice controller", icon: "🔗", color: COLORS.cyan,
      watches: "Services and Pods",
      does: "Keeps the list of healthy pod IPs behind each Service up to date. Pod dies → its IP disappears from the list within a loop or two. This is how Services stop sending traffic to dead pods.",
      hook: "The receptionist updating the phone directory.",
    },
    {
      name: "Namespace controller", icon: "🗑️", color: COLORS.danger,
      watches: "Namespaces marked Terminating",
      does: "Deletes every object inside a namespace before the namespace itself can disappear. A stuck namespace usually means a finalizer this controller is still waiting on.",
      hook: "The cleaner who won't lock up until the room is empty.",
    },
    {
      name: "PV binder controller", icon: "💾", color: COLORS.accent,
      watches: "PersistentVolumeClaims",
      does: "Matches a pending PVC to a suitable PV — or triggers dynamic provisioning via the StorageClass — and binds the two together.",
      hook: "The matchmaker pairing requests with storage.",
    },
    {
      name: "ServiceAccount + token", icon: "🎫", color: COLORS.purple,
      watches: "Namespaces and ServiceAccounts",
      does: "Makes sure every namespace has a default ServiceAccount and publishes the cluster CA bundle into each namespace so pods can trust the API server.",
      hook: "HR issuing an ID badge to every new hire.",
    },
    {
      name: "Garbage collector", icon: "♻️", color: COLORS.textMuted,
      watches: "ownerReferences on every object",
      does: "Deletes children whose owner is gone. Delete a Deployment → its ReplicaSet is orphaned → its pods are orphaned → all cleaned up automatically in that order.",
      hook: "Cut the branch, the leaves fall too.",
    },
  ];

  return (
    <Section
      num="04"
      title="MEET THE FAMILY"
      subtitle="kube-controller-manager is one binary running ~30 of these loops side by side. Here are the ones that matter for CKA. Tap any card."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {controllers.map((c, i) => {
          const isOpen = open === i;
          return (
            <div key={i} onClick={() => setOpen(isOpen ? null : i)} style={{
              background: isOpen ? `${c.color}11` : COLORS.bgCard,
              border: `1px solid ${isOpen ? c.color : COLORS.border}`,
              borderRadius: 8, padding: "12px 16px", cursor: "pointer", transition: "all 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 17 }}>{c.icon}</span>
                <span style={{ fontFamily: FONT, fontSize: 13, color: c.color, fontWeight: 600, flex: 1 }}>{c.name}</span>
                <span style={{ fontSize: 12, color: COLORS.textDim, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
              </div>
              {isOpen && (
                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.cyan }}>
                    👀 watches: <span style={{ color: COLORS.text }}>{c.watches}</span>
                  </div>
                  <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65, margin: 0 }}>
                    {c.does}
                  </p>
                  <div style={{
                    fontFamily: FONT_BODY, fontSize: 12, color: c.color, fontStyle: "italic",
                    borderLeft: `2px solid ${c.color}`, paddingLeft: 10,
                  }}>
                    🧠 {c.hook}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function InternalsSection() {
  const [stage, setStage] = useState(0);
  const stages = [
    { label: "WATCH", icon: "📡", color: COLORS.accent, text: "The controller opens a long-lived watch on the API Server for its resource type. It doesn't poll in a busy loop — the API Server pushes changes to it." },
    { label: "CACHE", icon: "🗄️", color: COLORS.purple, text: "An informer keeps a local in-memory copy of those objects. Reads are answered from this cache, so 30 controllers don't hammer etcd with the same queries." },
    { label: "QUEUE", icon: "📥", color: COLORS.cyan, text: "Each change drops a key like 'default/web-app' into a work queue. Duplicates collapse into one entry, so a burst of 50 events becomes one unit of work." },
    { label: "RECONCILE", icon: "⚖️", color: COLORS.warning, text: "A worker pops the key, fetches the CURRENT object, compares spec to status, and decides the action. It looks at the world as it is now — not at the event that woke it up." },
    { label: "ACT + REQUEUE", icon: "✍️", color: COLORS.success, text: "It writes to the API Server (create pod, update status). If the write failed or work remains, the key goes back on the queue with exponential backoff. Then round again." },
  ];
  const s = stages[stage];

  return (
    <Section
      num="05"
      title="INSIDE ONE LOOP"
      subtitle="What one turn of the wheel actually looks like in the code. Click a stage."
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {stages.map((st, i) => (
          <button key={i} onClick={() => setStage(i)} style={{
            flex: "1 1 90px", fontFamily: FONT, fontSize: 10.5, padding: "10px 6px", borderRadius: 8,
            cursor: "pointer", fontWeight: 700, letterSpacing: 0.5,
            background: i === stage ? `${st.color}22` : COLORS.bgCard,
            border: `1px solid ${i === stage ? st.color : COLORS.border}`,
            color: i === stage ? st.color : COLORS.textDim, transition: "all 0.3s",
          }}>
            <div style={{ fontSize: 16, marginBottom: 3 }}>{st.icon}</div>
            {st.label}
          </button>
        ))}
      </div>
      <div style={{
        background: COLORS.bgCard, border: `1px solid ${s.color}`, borderRadius: 10, padding: 18,
      }}>
        <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text, lineHeight: 1.75, margin: 0 }}>
          {s.text}
        </p>
      </div>

      <div style={{
        marginTop: 12, background: COLORS.bgCardAlt, borderRadius: 8, padding: "12px 16px",
        borderLeft: `3px solid ${COLORS.cyan}`,
      }}>
        <div style={{ fontFamily: FONT, fontSize: 11.5, color: COLORS.cyan, marginBottom: 6 }}>
          🔑 Why this design is so robust
        </div>
        <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7, margin: 0 }}>
          The loop is <strong style={{ color: COLORS.text }}>level-triggered</strong>, not edge-triggered. It reacts to <em>the state it sees</em>, not to <em>the event it missed</em>. So a controller can crash, miss a hundred events, restart, and still land on the correct answer — it just looks at the world again and fixes whatever is off. That's why Kubernetes survives its own components dying.
        </p>
      </div>
    </Section>
  );
}

function NodeClockSection() {
  const [t, setT] = useState(0);
  const marks = [
    { at: 0, label: "Node healthy", detail: "kubelet renews its Lease object every 10s. The node controller checks all nodes every 5s (--node-monitor-period).", color: COLORS.success },
    { at: 40, label: "Marked NotReady", detail: "No heartbeat for ~40s (--node-monitor-grace-period, higher in newer releases). Node status flips to NotReady. Pods are still running — nothing has been killed yet.", color: COLORS.warning },
    { at: 45, label: "NoExecute taint applied", detail: "The node gets node.kubernetes.io/unreachable:NoExecute. Every pod carries a default toleration of 300s for this taint, so a countdown starts.", color: COLORS.danger },
    { at: 345, label: "Pods evicted & rescheduled", detail: "Toleration expires (~5 min later). Pods are deleted from the dead node; if they belong to a ReplicaSet, that controller creates replacements on healthy nodes.", color: COLORS.purple },
  ];
  const active = marks[t];

  return (
    <Section
      num="06"
      title="THE NODE CONTROLLER CLOCK"
      subtitle="Exam favourite: 'a node dies — how long until pods move?' Walk the timeline."
    >
      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ position: "relative", height: 6, background: COLORS.border, borderRadius: 3, margin: "10px 0 28px" }}>
          <div style={{
            position: "absolute", left: 0, top: 0, height: "100%", borderRadius: 3,
            width: `${(t / (marks.length - 1)) * 100}%`, background: active.color, transition: "all 0.4s",
          }} />
          {marks.map((m, i) => (
            <button key={i} onClick={() => setT(i)} style={{
              position: "absolute", left: `${(i / (marks.length - 1)) * 100}%`, top: -7,
              transform: "translateX(-50%)", width: i === t ? 20 : 14, height: i === t ? 20 : 14,
              borderRadius: "50%", cursor: "pointer", padding: 0,
              background: i <= t ? m.color : COLORS.bgCardAlt,
              border: `2px solid ${i <= t ? m.color : COLORS.border}`, transition: "all 0.3s",
            }}>
              <span style={{
                position: "absolute", top: 26, left: "50%", transform: "translateX(-50%)",
                fontFamily: FONT, fontSize: 10, color: i === t ? m.color : COLORS.textDim, whiteSpace: "nowrap",
              }}>
                {m.at === 0 ? "0s" : m.at < 60 ? `~${m.at}s` : `~${Math.round(m.at / 60)}m`}
              </span>
            </button>
          ))}
        </div>

        <div style={{
          background: COLORS.bgCardAlt, borderRadius: 8, padding: 16, borderLeft: `3px solid ${active.color}`,
        }}>
          <div style={{ fontFamily: FONT, fontSize: 13, color: active.color, fontWeight: 700, marginBottom: 6 }}>
            {active.label}
          </div>
          <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7, margin: 0 }}>
            {active.detail}
          </p>
        </div>
      </div>
      <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7, marginTop: 12 }}>
        ⚠️ These defaults shift between releases — always confirm with{" "}
        <code style={{ fontFamily: FONT, color: COLORS.cyan }}>kubectl -n kube-system describe pod kube-controller-manager-...</code>{" "}
        on the cluster in front of you.
      </p>
    </Section>
  );
}

function RuntimeSection() {
  const [tab, setTab] = useState(0);
  const tabs = ["Vanilla K8s", "Key flags", "HA & leader election", "OpenShift"];

  const panels = [
    (
      <div key="a">
        <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.75, marginTop: 0 }}>
          On a kubeadm cluster, kube-controller-manager runs as a <strong style={{ color: COLORS.text }}>static pod</strong> on every control plane node. The kubelet starts it directly from a file on disk — which is why it can come up before the API Server is available.
        </p>
        <Terminal>{`# the manifest kubelet watches
/etc/kubernetes/manifests/kube-controller-manager.yaml

kubectl -n kube-system get pods -l component=kube-controller-manager
kubectl -n kube-system logs kube-controller-manager-master`}</Terminal>
        <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7 }}>
          Edit that YAML and the kubelet restarts the pod within seconds. Break the YAML and the pod never comes back — a classic troubleshooting scenario.
        </p>
      </div>
    ),
    (
      <div key="b">
        <Terminal>{`--controllers=*,bootstrapsigner,tokencleaner
      # which loops to run; * = all defaults

--node-monitor-period=5s
      # how often node health is checked

--node-monitor-grace-period=40s
      # silence before a node is marked NotReady

--kubeconfig=/etc/kubernetes/controller-manager.conf
      # how it authenticates to the API Server

--leader-elect=true
      # only one instance acts at a time

--use-service-account-credentials=true
      # each loop gets its own identity + RBAC`}</Terminal>
        <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7 }}>
          That last flag matters for CKS: with it, each controller authenticates as its own ServiceAccount, so a compromised loop only holds the permissions it actually needs.
        </p>
      </div>
    ),
    (
      <div key="c">
        <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.75, marginTop: 0 }}>
          In an HA cluster you run three copies — but they must not all reconcile at once, or three of them would each create the missing pod. So they hold an election.
        </p>
        <Terminal>{`kubectl -n kube-system get lease kube-controller-manager -o yaml
# holderIdentity: master-1_8f2c...
# renewTime: refreshed every few seconds`}</Terminal>
        <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7 }}>
          One instance holds the Lease and does all the work. The others sit idle, watching the clock. If the holder stops renewing, a standby grabs the Lease and takes over. <strong style={{ color: COLORS.text }}>Active-passive, not active-active.</strong>
        </p>
      </div>
    ),
    (
      <div key="d">
        <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.textMuted, lineHeight: 1.75, marginTop: 0 }}>
          OpenShift runs the same upstream controllers, but you don't hand-edit the manifest — an <strong style={{ color: COLORS.text }}>operator</strong> owns it and rolls out changes as numbered revisions. There's also a second controller manager for OpenShift's own resource types.
        </p>
        <Terminal>{`oc get pods -n openshift-kube-controller-manager
oc get pods -n openshift-controller-manager
      # BuildConfig, DeploymentConfig, ImageStream loops

oc get clusteroperator kube-controller-manager
oc edit kubecontrollermanager cluster
      # change config here, never the static pod YAML`}</Terminal>
        <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7 }}>
          Same loop, same philosophy — pushed one level higher. The operator itself is a controller whose desired state is "kube-controller-manager is configured correctly."
        </p>
      </div>
    ),
  ];

  return (
    <Section num="07" title="WHERE IT ACTUALLY RUNS" subtitle="The operational side — what you'd touch on a real cluster or in the exam.">
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {tabs.map((label, i) => (
          <button key={i} onClick={() => setTab(i)} style={{
            fontFamily: FONT, fontSize: 11, padding: "7px 12px", borderRadius: 6, cursor: "pointer",
            background: i === tab ? COLORS.accentGlow : "transparent",
            border: `1px solid ${i === tab ? COLORS.accent : COLORS.border}`,
            color: i === tab ? COLORS.accent : COLORS.textDim, transition: "all 0.25s",
          }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: 18 }}>
        {panels[tab]}
      </div>
    </Section>
  );
}

function QuizSection() {
  const questions = [
    {
      q: "You delete a pod that belongs to a Deployment. Which controller brings it back?",
      options: ["The Deployment controller", "The ReplicaSet controller", "The scheduler", "The kubelet"],
      answer: 1,
      why: "The Deployment controller only manages ReplicaSets. The ReplicaSet controller is the one counting pods and creating replacements.",
    },
    {
      q: "A controller notices it needs a new pod. What does it do?",
      options: [
        "SSH to the least busy node and start a container",
        "Ask the scheduler to pick a node, then start the container itself",
        "Create a Pod object in the API Server and stop there",
        "Tell the kubelet directly over gRPC",
      ],
      answer: 2,
      why: "Controllers only write to the API Server. The scheduler then assigns a node, and that node's kubelet actually starts the container. Three separate jobs.",
    },
    {
      q: "kube-controller-manager crashes for two minutes, then restarts. What happens to the events it missed?",
      options: [
        "They're replayed from a queue on disk",
        "Nothing — it re-reads current state and fixes whatever is wrong",
        "The cluster stays broken until you reconcile manually",
        "etcd rolls back to the last consistent state",
      ],
      answer: 1,
      why: "Level-triggered design. It doesn't need the missed events — it looks at the world as it is now and closes any gap it finds.",
    },
    {
      q: "In a 3-master HA cluster, how many kube-controller-managers are actively reconciling?",
      options: ["All three, sharded by namespace", "All three, with etcd resolving conflicts", "Exactly one — the Lease holder", "Zero until you elect one manually"],
      answer: 2,
      why: "Leader election via a Lease object in kube-system. One active, two standby. Otherwise three copies would each create the same missing pod.",
    },
    {
      q: "A node loses power. Roughly how long before its pods are recreated elsewhere?",
      options: ["Instantly", "~40 seconds", "~5–6 minutes", "Never, without manual intervention"],
      answer: 2,
      why: "~40s to be marked NotReady, then the NoExecute taint plus the default 300s toleration before eviction. Roughly five to six minutes total.",
    },
  ];

  const [picked, setPicked] = useState({});

  return (
    <Section num="08" title="CHECK YOURSELF" subtitle="Five questions. Answer before you scroll — the explanation appears after you pick.">
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {questions.map((item, qi) => {
          const sel = picked[qi];
          return (
            <div key={qi} style={{
              background: COLORS.bgCard, border: `1px solid ${sel === undefined ? COLORS.border : sel === item.answer ? COLORS.success : COLORS.danger}`,
              borderRadius: 10, padding: 16, transition: "border-color 0.3s",
            }}>
              <div style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.text, marginBottom: 12, lineHeight: 1.6 }}>
                <span style={{ fontFamily: FONT, color: COLORS.textDim, marginRight: 8 }}>Q{qi + 1}</span>
                {item.q}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {item.options.map((o, oi) => {
                  const isPicked = sel === oi;
                  const isRight = oi === item.answer;
                  const show = sel !== undefined;
                  let bc = COLORS.border, tc = COLORS.textMuted;
                  if (show && isRight) { bc = COLORS.success; tc = COLORS.success; }
                  else if (show && isPicked) { bc = COLORS.danger; tc = COLORS.danger; }
                  return (
                    <button key={oi} onClick={() => sel === undefined && setPicked({ ...picked, [qi]: oi })}
                      style={{
                        textAlign: "left", fontFamily: FONT_BODY, fontSize: 12.5, padding: "8px 12px",
                        borderRadius: 6, cursor: sel === undefined ? "pointer" : "default",
                        background: show && isRight ? COLORS.successGlow : "transparent",
                        border: `1px solid ${bc}`, color: tc, transition: "all 0.25s",
                      }}>
                      {o}
                    </button>
                  );
                })}
              </div>
              {sel !== undefined && (
                <p style={{
                  fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.7,
                  marginTop: 12, marginBottom: 0, borderLeft: `2px solid ${COLORS.cyan}`, paddingLeft: 10,
                }}>
                  {sel === item.answer ? "✅ " : "❌ "}{item.why}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function HooksSection() {
  const hooks = [
    { icon: "🌡️", text: "A controller is a thermostat for one kind of object", color: COLORS.accent },
    { icon: "🔁", text: "Watch → compare → act → repeat, forever", color: COLORS.cyan },
    { icon: "📝", text: "spec = what you want · status = what is · gap = the job", color: COLORS.purple },
    { icon: "✍️", text: "Controllers write objects; kubelets run containers", color: COLORS.danger },
    { icon: "📜", text: "Deployment → ReplicaSet → Pod (each layer has its own loop)", color: COLORS.success },
    { icon: "👑", text: "HA = many copies, one Lease holder, active-passive", color: COLORS.warning },
  ];
  return (
    <Section num="09" title="MEMORY HOOKS" subtitle="When the detail fades, these six lines rebuild it.">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
        {hooks.map((h, i) => (
          <div key={i} style={{
            background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderLeft: `3px solid ${h.color}`,
            borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ fontSize: 19, minWidth: 26, textAlign: "center" }}>{h.icon}</span>
            <span style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.text, lineHeight: 1.5 }}>{h.text}</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function K8sController() {
  return (
    <div style={{
      background: COLORS.bg, minHeight: "100vh", padding: "28px 40px",
      color: COLORS.text,
    }}>
      <div style={{ textAlign: "center", marginBottom: 34 }}>
        <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.cyan, letterSpacing: 4, marginBottom: 6 }}>
          KUBERNETES DEEP DIVE
        </div>
        <h1 style={{
          fontFamily: FONT, fontSize: 26, margin: "0 0 8px",
          background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.cyan})`,
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          Controllers &amp; the Control Loop
        </h1>
        <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: COLORS.textMuted, maxWidth: 500, margin: "0 auto", lineHeight: 1.6 }}>
          The part of the control plane that makes the cluster self-healing — and it's just a while-loop with good manners.
        </p>
      </div>

      <ConceptSection />
      <AnalogySection />
      <SimulatorSection />
      <FamilySection />
      <InternalsSection />
      <NodeClockSection />
      <RuntimeSection />
      <QuizSection />
      <HooksSection />

      <div style={{
        textAlign: "center", fontFamily: FONT, fontSize: 10, color: COLORS.textDim,
        paddingTop: 16, borderTop: `1px solid ${COLORS.border}`,
      }}>
        K8s Controllers — Interactive Learning Module
      </div>
    </div>
  );
}
